import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Context value for synchronized zoom state across multiple charts.
 * Used by charts inside a SyncedChartProvider to share zoom domain.
 */
export interface SyncedZoomContextValue {
  /**
   * Current zoom domain shared across all synced charts.
   * - For time/linear scales: [start, end] as numbers
   * - For band scales: array of category strings (subset of domain)
   * - null if not zoomed
   */
  zoomDomain: [number, number] | string[] | null;

  /**
   * Sets the zoom domain for all synced charts.
   * - For time/linear scales: Pass [start, end] as numbers
   * - For band scales: Pass array of category strings (subset of domain)
   * - Pass null to reset
   */
  setZoomDomain: (domain: [number, number] | string[] | null) => void;

  /**
   * Resets the zoom to show all data across all synced charts.
   */
  resetZoom: () => void;
}

const SyncedZoomContext = createContext<SyncedZoomContextValue | null>(null);

export interface SyncedZoomProviderProps {
  children: ReactNode;
}

/**
 * Provider for synchronized zoom state.
 * Internal component used by SyncedChartProvider.
 */
export function SyncedZoomProvider({ children }: SyncedZoomProviderProps) {
  const [zoomDomain, setZoomDomainState] = useState<[number, number] | string[] | null>(null);

  const setZoomDomain = useCallback((domain: [number, number] | string[] | null) => {
    setZoomDomainState(domain);
  }, []);

  const resetZoom = useCallback(() => {
    setZoomDomainState(null);
  }, []);

  const contextValue = useMemo<SyncedZoomContextValue>(
    () => ({
      zoomDomain,
      setZoomDomain,
      resetZoom,
    }),
    [zoomDomain, setZoomDomain, resetZoom],
  );

  return <SyncedZoomContext.Provider value={contextValue}>{children}</SyncedZoomContext.Provider>;
}

/**
 * Hook to access the synced zoom context.
 * Returns null if not inside a SyncedChartProvider (chart operates independently).
 *
 * @returns The synced zoom context value, or null if not synced
 */
export function useSyncedZoomContext(): SyncedZoomContextValue | null {
  return useContext(SyncedZoomContext);
}
